import { Tooltip } from "@coval/ui"
import { AlertTriangle } from "lucide-react"

import type { OutlierEvidence } from "../../utils/buildOutlierEvidence"

interface OutlierExplanationTooltipProps {
  evidence: readonly OutlierEvidence[]
  rowNumber: number
}

const numberFormatter = new Intl.NumberFormat(undefined, {
  maximumSignificantDigits: 2,
})

function formatNumber(value: number): string {
  return numberFormatter.format(value)
}

function getFenceDescription(item: OutlierEvidence): string {
  const relation = item.direction === "high" ? "above" : "below"
  const fenceClass = item.severity === "mild" ? "inner" : "outer"

  return item.fence == null
    ? `${formatNumber(item.value)} is ${relation} the ${item.direction} threshold`
    : `${formatNumber(item.value)} is ${relation} the ${fenceClass} ${item.direction} fence (${formatNumber(item.fence)})`
}

export function OutlierExplanationTooltip({
  evidence,
  rowNumber,
}: OutlierExplanationTooltipProps) {
  const metricCount = evidence.length
  const tooltipContent = (
    <div className="max-w-xs space-y-2 p-1">
      <p className="font-medium text-coval-warning-text">
        {metricCount} outlier {metricCount === 1 ? "metric" : "metrics"}
      </p>
      <ul className="space-y-1.5">
        {evidence.map((item, index) => (
          <li key={`${item.metricId}-${item.metricVersionNumber}-${index}`}>
            <p className="font-medium">
              {item.metricLabel}
              {item.metricVersionNumber != null
                ? ` · v${item.metricVersionNumber}`
                : ""}
            </p>
            <p className="text-coval-text-muted">
              {getFenceDescription(item)} · {item.severity} outlier
            </p>
          </li>
        ))}
      </ul>
    </div>
  )

  return (
    <Tooltip content={tooltipContent} side="right">
      <button
        type="button"
        aria-label={`Show outlier details for result ${rowNumber}`}
        className="inline-flex items-center rounded-sm text-coval-warning-on-surface hover:text-coval-warning-on-surface focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-coval-warning-on-surface"
      >
        <AlertTriangle aria-hidden className="size-3.5" />
      </button>
    </Tooltip>
  )
}
